import type { ReadinessBreakdown, ReadinessProfile } from '../../types/readiness';
import { getYearsExperienceForReadiness } from '../experienceRange';
import { EMS_SKILL_OPTIONS } from '../emsSkills';
import {
  ACLS_PATTERNS,
  BLS_PATTERNS,
  PALS_PATTERNS,
  PHTLS_PATTERNS,
  NREMT_PATTERNS,
  STATE_LICENSE_PATTERNS,
  daysUntilExpiry,
  extractCertifications,
  formatExpiryLabel,
  isExpired,
  certMatchesPattern,
  certCounts,
  certNotCountingReason,
  scoringClock,
  scoringDateKey,
  scoringToday,
  calendarDaysBetween,
} from './certUtils';
import { resolveDocumentVerification } from '../credentials/certificationChecks';
import { isDocumentReviewed } from '../credentials/documentReviewGate';
import { isCredentialDocumentCategory, isMedicalDocumentCategory } from '../documents/documentCategories';
import { documentCardExpiryDay } from '../renewal/credentialExpiry';
import type { Certification } from '../../types/readiness';
import { STATE_EMS_CERTIFICATION, resolveCanonicalCertification, stateCertificationNote } from '../constants/certifications';

const SKILL_CATEGORIES: Record<string, string[]> = {
  airway: ['Intubation', 'Supraglottic Airway', 'CPAP/BiPAP', 'Suction'],
  cardiac: ['12-Lead ECG', 'Cardiac Monitoring', 'Defibrillation', 'Pacing'],
  trauma: ['Trauma Assessment', 'Hemorrhage Control', 'Spinal Immobilization', 'Tourniquet'],
  pediatric: ['Pediatric Assessment', 'PALS'],
  ob: ['OB Emergencies', 'Neonatal Resuscitation'],
  behavioral: ['Behavioral Emergencies', 'Crisis Intervention'],
};

/** Documents the employer arranges (B2): never scored for or against an EMT. */
const NOT_SCORED_DOCUMENT_CATEGORIES: readonly string[] = ['backgroundCheck', 'drugScreen', 'drivingRecord'];

const REQUIRED_DOC_SPECS: Array<{
  type: string;
  label: string;
  required: boolean;
  categories: string[];
  certPatterns?: RegExp[];
}> = [
  { type: 'nremt_card', label: 'EMT certification (NREMT or state)', required: true, categories: ['emtCertification', 'nremtCertification'], certPatterns: NREMT_PATTERNS },
  { type: 'state_license', label: 'State license', required: true, categories: ['stateLicense'], certPatterns: STATE_LICENSE_PATTERNS },
  { type: 'bls_card', label: 'BLS card', required: true, categories: ['cprBls'], certPatterns: BLS_PATTERNS },
  { type: 'driver_license', label: "Driver's license", required: true, categories: ['driversLicense'] },
  { type: 'acls_card', label: 'ACLS card', required: false, categories: [], certPatterns: ACLS_PATTERNS },
  { type: 'pals_card', label: 'PALS card', required: false, categories: [], certPatterns: PALS_PATTERNS },
  // No immunization, TB or COVID records (2026-10-10, B1): nothing medical is counted before a job
  // offer. The optional part of Documentation is ACLS and PALS cards only.
];

function docUploaded(
  profile: ReadinessProfile,
  categories: string[],
  certPatterns?: RegExp[]
): { uploaded: boolean; expiryDate?: string; label: string } {
  const docs = profile.profileDocuments ?? [];
  // A credential-category document only counts once the server-side check has actually passed it.
  // It used to be enough to have a downloadUrl, so ANY accepted file — a landscape photo uploaded to
  // "NREMT certification" — scored the full 20 points for that required document and reported
  // "✓ NREMT card uploaded". Non-credential categories (immunization record, TB test) get no
  // credential check and are still counted on upload. A publicProfiles entry has no file link (the
  // employer-side job match reads that mirror), only hasFile.
  const matchDoc = docs.find((d) => {
    if (!categories.includes(d.category || '') || !(d.downloadUrl || d.hasFile === true)) return false;
    if (!isCredentialDocumentCategory(d.category)) return true;
    return isDocumentReviewed(resolveDocumentVerification(d, profile.certificationChecks));
  });
  if (matchDoc) {
    return {
      uploaded: true,
      // A profile document has no typed expiry (the upload asks for none), so without the date the
      // check read off the card an expired driver's or state license stayed "uploaded" forever.
      expiryDate: matchDoc.expiresAt || documentCardExpiryDay(matchDoc, profile.certificationChecks) || undefined,
      label: matchDoc.fileName || categories[0] || 'document',
    };
  }
  if (certPatterns) {
    const certs = extractCertifications(profile);
    // Only a certification whose document passed review counts as its document (before 2026-10-13
    // any uploaded file did, so a pending or rejected upload filled a required document slot).
    const matchCert = certs.find((c) => certMatchesPattern(c.name, certPatterns) && c.fileUrl && isDocumentReviewed(c.aiVerification));
    if (matchCert) {
      return { uploaded: true, expiryDate: matchCert.expiryDate, label: matchCert.name };
    }
  }
  return { uploaded: false, label: categories[0] || 'document' };
}

/**
 * An active State EMS Certification whose state keeps the license without a current NREMT at its
 * level (no level recorded = EMT), with the sentence that says so. Null when there is none: no state
 * recorded, an unknown state, a state that requires NREMT (SC, UT), or expired.
 */
function stateCertStandingInForNremt(certs: Certification[], now: Date): { cert: Certification; note: string } | null {
  for (const c of certs) {
    if (resolveCanonicalCertification(c.name) !== STATE_EMS_CERTIFICATION) continue;
    if (!certCounts(c, now)) continue;
    const note = stateCertificationNote(c.state, c.level);
    if (note) return { cert: c, note };
  }
  return null;
}

/**
 * Does this profile contain anything the EMT actually filled in? Used to stop the
 * "profile completeness" pillar from paying out for merely having an account (see
 * scoreProfileActivity). Deliberately generous — any one real field counts.
 */
function hasAnyProfileContent(profile: ReadinessProfile, now: Date): boolean {
  const pro = profile.professional;
  const skills = pro?.skills;
  const skillCount = Array.isArray(skills)
    ? skills.length
    : skills && typeof skills === 'object'
      ? Object.keys(skills).filter((k) => skills[k] === true).length
      : 0;
  // A credential document the check REJECTED is not profile content — counting it would let a
  // rejected upload buy back the points this pillar was just stopped from giving away.
  // Nothing medical counts (B1), and nothing the employer arranges (B2: background check, drug screen,
  // driving record): an immunization record or a drug screen on file must never move any score, and
  // either one used to switch this part on (2026-10-13 logic audit).
  const usableDocs = (profile.profileDocuments ?? []).filter(
    (d) =>
      !isMedicalDocumentCategory(d.category) &&
      !NOT_SCORED_DOCUMENT_CATEGORIES.includes(d.category || '') &&
      (!isCredentialDocumentCategory(d.category) ||
        resolveDocumentVerification(d, profile.certificationChecks)?.status !== 'rejected'),
  );
  return (
    extractCertifications(profile).length > 0 ||
    usableDocs.length > 0 ||
    (profile.pastExperience ?? []).length > 0 ||
    skillCount > 0 ||
    getYearsExperienceForReadiness(profile as Parameters<typeof getYearsExperienceForReadiness>[0], scoringToday(now)) > 0 ||
    (profile.bio || '').trim().length > 0 ||
    Boolean(profile.photoURL)
  );
}

/**
 * Credentials (35% of readiness). A credential earns its points only when it COUNTS (certUtils
 * certCounts: on the profile, document reviewed, not expired on the EMT's calendar). Points: the
 * level credential (NREMT, or EMT-B / AEMT / Paramedic) 35, the state license 25, BLS 20, ACLS 8,
 * PALS 6, PHTLS 6 (capped at 100). A NREMT or state license that has expired, with no current one that
 * counts, caps this part at 50. Before 2026-10-13 a credential typed with no document scored in full
 * and a rejected document still earned 90% of its points.
 */
function scoreCertifications(profile: ReadinessProfile, now: Date): ReadinessBreakdown['pillars']['certifications'] {
  const certs = extractCertifications(profile);
  const details: string[] = [];
  let points = 0;
  const maxPoints = 100;

  const held = (patterns: RegExp[]) => certs.filter((c) => certMatchesPattern(c.name, patterns));
  const counting = (patterns: RegExp[]) => held(patterns).find((c) => certCounts(c, now));
  /** Held but not counting: 'expired' when a reviewed one expired, else 'not_reviewed'. */
  const notCounting = (patterns: RegExp[]): 'expired' | 'not_reviewed' | null => {
    const list = held(patterns);
    if (!list.length) return null;
    if (list.some((c) => isExpired(c.expiryDate, now))) return 'expired';
    return list.map((c) => certNotCountingReason(c, now)).find((r) => r != null) ?? null;
  };

  const nremt = counting(NREMT_PATTERNS);
  const state = counting(STATE_LICENSE_PATTERNS);
  // A state that keeps licenses without a current NREMT (STATE_NREMT_RULES, 2026-10-10): there a
  // State EMS Certification for that state that counts, at its level, IS the level credential, so it
  // fills the NREMT / level bucket. An EMT is never marked down for not holding a NREMT the state
  // does not require. A state certification with no state recorded keeps the plain scoring.
  const stateStandIn = nremt ? null : stateCertStandingInForNremt(certs, now);
  const nremtExpired = !nremt && !stateStandIn && notCounting(NREMT_PATTERNS) === 'expired';
  const stateExpired = !state && notCounting(STATE_LICENSE_PATTERNS) === 'expired';

  if (nremt) {
    points += 35;
    details.push(`✓ ${nremt.name} active, ${formatExpiryLabel(nremt.expiryDate)}`);
  } else if (stateStandIn) {
    points += 35;
    details.push(`✓ ${stateStandIn.note}`);
  } else if (nremtExpired) {
    details.push('✗ EMT certification expired');
  } else if (notCounting(NREMT_PATTERNS) === 'not_reviewed') {
    details.push('✗ EMT certification document not reviewed yet');
  } else {
    details.push('✗ EMT certification not on file');
  }

  if (state) {
    points += 25;
    details.push('✓ State license active');
  } else if (stateExpired) {
    details.push('✗ State license expired');
  } else if (notCounting(STATE_LICENSE_PATTERNS) === 'not_reviewed') {
    details.push('✗ State license document not reviewed yet');
  } else {
    details.push('✗ State license not on file');
  }

  const bls = counting(BLS_PATTERNS);
  if (bls) {
    points += 20;
    details.push(`✓ BLS active, ${formatExpiryLabel(bls.expiryDate)}`);
  } else if (notCounting(BLS_PATTERNS) === 'expired') {
    details.push('✗ BLS expired');
  } else if (notCounting(BLS_PATTERNS) === 'not_reviewed') {
    details.push('✗ BLS document not reviewed yet');
  } else {
    details.push('✗ BLS not active');
  }

  if (counting(ACLS_PATTERNS)) {
    points += 8;
    details.push('✓ ACLS active');
  } else {
    details.push('✗ ACLS not active');
  }
  if (counting(PALS_PATTERNS)) {
    points += 6;
    details.push('✓ PALS active');
  }
  if (counting(PHTLS_PATTERNS)) {
    points += 6;
    details.push('✓ PHTLS active');
  }

  let score = Math.min(maxPoints, points);
  if (nremtExpired || stateExpired) {
    score = Math.min(score, 50);
    details.push('⚠️ Core credential expired: pillar capped at 50%');
  }

  return { score, weight: 35, details };
}

function scoreDocumentation(profile: ReadinessProfile, now: Date): ReadinessBreakdown['pillars']['documentation'] {
  const details: string[] = [];
  const required: typeof REQUIRED_DOC_SPECS = REQUIRED_DOC_SPECS.filter((d) => d.required);
  const optional = REQUIRED_DOC_SPECS.filter((d) => !d.required);

  let reqUploaded = 0;
  let reqExpired = false;
  // Same state rule as the Credentials part: where a state certification stands in for NREMT, its
  // reviewed state license document stands in for the NREMT card.
  const stateStandIn = stateCertStandingInForNremt(extractCertifications(profile), now);
  for (const spec of required) {
    let st = docUploaded(profile, spec.categories, spec.certPatterns);
    let standIn = false;
    if (!st.uploaded && spec.type === 'nremt_card' && stateStandIn) {
      const stateDoc = docUploaded(profile, ['stateLicense'], STATE_LICENSE_PATTERNS);
      if (stateDoc.uploaded) {
        st = stateDoc;
        standIn = true;
      }
    }
    const label = standIn ? `State EMS Certification (${stateStandIn?.cert.state ?? ''}), in place of NREMT` : spec.label;
    if (st.uploaded) {
      reqUploaded += 1;
      if (isExpired(st.expiryDate, now)) {
        reqExpired = true;
        details.push(`✗ ${label} expired`);
      } else {
        const days = daysUntilExpiry(st.expiryDate, now);
        const warn = days != null && days <= 30 ? ' ⚠️' : '';
        details.push(`✓ ${label} uploaded${warn}`);
      }
    } else {
      details.push(`✗ ${spec.label} missing`);
    }
  }

  let optUploaded = 0;
  for (const spec of optional) {
    const st = docUploaded(profile, spec.categories, spec.certPatterns);
    if (st.uploaded) {
      optUploaded += 1;
      details.push(`✓ ${spec.label} on file`);
    }
  }

  const reqScore = required.length ? (reqUploaded / required.length) * 80 : 0;
  const optScore = optional.length ? (optUploaded / optional.length) * 20 : 0;
  let score = Math.round(reqScore + optScore);
  if (reqExpired) {
    score = Math.round(score * 0.4);
    details.push('⚠️ Required document expired: documentation pillar reduced');
  }

  return { score: Math.min(100, score), weight: 25, details };
}

function scoreExperience(profile: ReadinessProfile, now: Date): ReadinessBreakdown['pillars']['experience'] {
  const years = getYearsExperienceForReadiness(profile as Parameters<typeof getYearsExperienceForReadiness>[0], scoringToday(now));
  // Only EMS roles earn experience points and role variety; jobs outside EMS are work history only.
  const past = (profile.pastExperience ?? []).filter((e) => (e as { outsideEms?: boolean }).outsideEms !== true);
  // An EMT who has told us nothing about their experience scores zero. This used to start at an
  // unconditional 30, so a blank profile was credited 30/100 here. `years === 0` with no past
  // roles on file means "no experience data", which is exactly the case that must not earn points.
  const hasExperienceData = years > 0 || past.length > 0;
  let base = 0;
  if (years >= 5) base = 100;
  else if (years >= 3) base = 85;
  else if (years >= 2) base = 70;
  else if (years >= 1) base = 50;
  else if (hasExperienceData) base = 30;

  const roleSet = new Set<string>();
  for (const e of past) {
    const st = (e.serviceType || '').toLowerCase();
    if (/911|emergency/i.test(st)) roleSet.add('911');
    if (/ift|interfacility/i.test(st)) roleSet.add('IFT');
    if (/event/i.test(st)) roleSet.add('event');
    if (/fire/i.test(st)) roleSet.add('fire');
    // Whole word only: /er/ alone matched "Other" (and "emergency", "interfacility"), so those earned an
    // "ER tech" variety bonus (2026-10-13 logic audit).
    if (/hospital|\ber\b/i.test(st)) roleSet.add('ER tech');
    if (/supervisor|lead/i.test(st)) roleSet.add('supervisor');
    // Without these, the two newer service types would earn zero role-variety credit — an EMT
    // with 911 + community paramedicine experience would score below one with 911 + IFT for
    // equally varied work. varietyBonus stays capped at 15, so this can't inflate any score
    // past the existing ceiling; it only lets these roles count toward it. Neither pattern
    // overlaps an existing bucket's regex, so no already-stored serviceType changes buckets.
    if (/community paramedic|mobile integrated|\bmih\b/i.test(st)) roleSet.add('community paramedicine');
    if (/behavioral|co-response|coresponse|crisis/i.test(st)) roleSet.add('behavioral health');
  }
  const varietyBonus = Math.min(15, roleSet.size * 5);
  const score = Math.min(100, base + varietyBonus);
  const details = [
    `${years} year${years === 1 ? '' : 's'} EMS experience`,
    roleSet.size > 0 ? `Role variety: ${[...roleSet].join(', ')}` : 'Add past roles for variety bonus',
  ];
  return { score, weight: 20, details };
}

function scoreSkills(profile: ReadinessProfile): ReadinessBreakdown['pillars']['skills'] {
  const raw = profile.professional?.skills;
  let skillNames: string[] = [];
  if (Array.isArray(raw)) skillNames = raw;
  else if (raw && typeof raw === 'object') {
    skillNames = Object.keys(raw).filter((k) => raw[k] === true);
  }

  const count = skillNames.length;
  // No skills selected must score zero. This pillar used to start at an unconditional 40, so a
  // brand-new profile with nothing filled in was credited 40/100 under "Skills" (and, at 15%
  // weight, 6 points of overall readiness) for data that did not exist.
  let score = 0;
  if (count >= 15) score = 100;
  else if (count >= 10) score = 80;
  else if (count >= 5) score = 60;
  else if (count > 0) score = Math.round((count / 5) * 40);

  const categoriesHit = new Set<string>();
  for (const [cat, keywords] of Object.entries(SKILL_CATEGORIES)) {
    if (skillNames.some((s) => keywords.some((k) => s.toLowerCase().includes(k.toLowerCase())))) {
      categoriesHit.add(cat);
    }
  }
  if (categoriesHit.size >= 3) score = Math.min(100, score + 10);

  const details = [
    `${count} skill${count === 1 ? '' : 's'} selected`,
    categoriesHit.size > 0 ? `Categories: ${[...categoriesHit].join(', ')}` : `Add skills from ${EMS_SKILL_OPTIONS.length}+ options`,
  ];
  return { score, weight: 15, details };
}

function scoreProfileActivity(profile: ReadinessProfile, now: Date): ReadinessBreakdown['pillars']['profileActivity'] {
  let score = 0;
  const details: string[] = [];
  if (profile.photoURL) {
    score += 25;
    details.push('✓ Profile photo');
  } else {
    details.push('✗ Add a profile photo');
  }
  if ((profile.bio || '').trim().length > 20) {
    score += 25;
    details.push('✓ Bio completed');
  } else {
    details.push('✗ Add a bio');
  }
  // "Recently active" and "public profile enabled" are both true the instant an account is created
  // (updatedAt is stamped at signup and publicProfile defaults to on), so on their own they handed a
  // brand-new, entirely empty profile 50/100 here. Both now require the profile to actually contain
  // something — an empty profile being publicly visible and recently touched is not readiness.
  const hasContent = hasAnyProfileContent(profile, now);
  const updated = profile.updatedAt as { toMillis?: () => number } | undefined;
  const updatedMs = updated?.toMillis?.() ?? 0;
  // Calendar days on the EMT's own calendar (the scoring clock), so it flips at their midnight like
  // every other date rule, and the stored score can be refreshed at that moment.
  const daysSinceUpdate = updatedMs ? calendarDaysBetween(scoringDateKey(updatedMs, now), scoringToday(now)) : 999;
  if (!hasContent) {
    details.push('✗ Add your credentials, experience or skills to start earning this');
    return { score, weight: 5, details };
  }
  if (daysSinceUpdate <= 30) {
    score += 25;
    details.push('✓ Active in last 30 days');
  } else {
    details.push('✗ Log in to keep profile active');
  }
  if (profile.preferences?.publicProfile !== false) {
    score += 25;
    details.push('✓ Public profile enabled');
  } else {
    details.push('✗ Enable public profile');
  }
  return { score, weight: 5, details };
}

function collectExpiryIssues(profile: ReadinessProfile, now: Date): Pick<ReadinessBreakdown, 'expiringDocs' | 'expiredDocs'> {
  const expiringDocs: ReadinessBreakdown['expiringDocs'] = [];
  const expiredDocs: ReadinessBreakdown['expiredDocs'] = [];

  for (const c of extractCertifications(profile)) {
    const days = daysUntilExpiry(c.expiryDate, now);
    if (days == null) continue;
    if (days < 0) expiredDocs.push({ type: c.name, expiryDate: c.expiryDate! });
    else if (days <= 30) expiringDocs.push({ type: c.name, expiryDate: c.expiryDate!, daysUntil: days });
  }

  for (const spec of REQUIRED_DOC_SPECS) {
    const st = docUploaded(profile, spec.categories, spec.certPatterns);
    if (!st.uploaded || !st.expiryDate) continue;
    const days = daysUntilExpiry(st.expiryDate, now);
    if (days == null) continue;
    if (days < 0) expiredDocs.push({ type: spec.label, expiryDate: st.expiryDate });
    else if (days <= 30) expiringDocs.push({ type: spec.label, expiryDate: st.expiryDate, daysUntil: days });
  }

  return { expiringDocs, expiredDocs };
}

export function calculateReadiness(profile: ReadinessProfile, nowIn: Date = new Date()): ReadinessBreakdown {
  // The EMT's own calendar (certUtils scoringClock): the same day on their browser and on the server.
  const now = scoringClock(profile, nowIn);
  const certifications = scoreCertifications(profile, now);
  const documentation = scoreDocumentation(profile, now);
  const experience = scoreExperience(profile, now);
  const skills = scoreSkills(profile);
  const profileActivity = scoreProfileActivity(profile, now);

  // Whole-number weights, divided once: 0.35 * 70 is 24.499999... in floating point, which used to
  // round a true x.5 total down (2026-10-13 logic audit). Halves round up.
  const total = Math.round(
    (certifications.score * 35 +
      documentation.score * 25 +
      experience.score * 20 +
      skills.score * 15 +
      profileActivity.score * 5) / 100
  );

  const { expiringDocs, expiredDocs } = collectExpiryIssues(profile, now);

  return {
    total: Math.min(100, Math.max(0, total)),
    pillars: { certifications, documentation, experience, skills, profileActivity },
    expiringDocs,
    expiredDocs,
  };
}

export function strongestPillar(breakdown: ReadinessBreakdown): string {
  const entries = Object.entries(breakdown.pillars) as [string, { score: number; weight: number }][];
  const best = entries.sort((a, b) => b[1].score - a[1].score)[0];
  const labels: Record<string, string> = {
    certifications: 'Credentials',
    documentation: 'Documentation',
    experience: 'Experience',
    skills: 'Skills',
    profileActivity: 'Profile',
  };
  return labels[best[0]] || 'Credentials';
}
